<template>
  <div class="ue-it-edit">
    <el-card>
      <div class="header d-flex justify-content-between align-items-center mb-3">
        <h2>ИТ (Год: {{ formData.trancheYear }})</h2>
        <div>
          <el-button @click="goBack" class="ms-2">Назад</el-button>
          <el-button type="primary" @click="saveData" :loading="saving" v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)">Сохранить</el-button>
        </div>
      </div>

      <div class="table-container mb-3" style="overflow-x: auto;" @paste="handlePaste" @keydown="handleKeydown">
        <table class="table table-bordered grid-table">
          <thead>
            <tr>
              <th rowspan="2">Наименование группы товаров</th>
              <th rowspan="2">Наименование продукта</th>
              <th rowspan="2">Количество</th>
              <th rowspan="2">Периодичность</th>
              <th rowspan="2">Стоимость за единицу, тенге</th>
              <th rowspan="2">Всего</th>
              <th colspan="12" style="padding: 0;">в т.ч. по месяцам</th>
              <th rowspan="2" v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)"></th>
            </tr>
            <tr>
              <th>янв</th>
              <th>фев</th>
              <th>мар</th>
              <th>апр</th>
              <th>май</th>
              <th>июн</th>
              <th>июл</th>
              <th>авг</th>
              <th>сен</th>
              <th>окт</th>
              <th>ноя</th>
              <th>дек</th>
            </tr>
          </thead>
          <tbody>
            <tr class="total-row calculated-row readonly-text">
              <td colspan="4">ВСЕГО:</td>
              <td>{{ formatNumber(grandTotal.costPerUnit) }}</td>
              <td>{{ formatNumber(grandTotal.total) }}</td>
              <td v-for="field in monthFields" :key="'gt-'+field">{{ formatNumber(grandTotal[field]) }}</td>
              <td v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)"></td>
            </tr>
          </tbody>
          <tbody v-for="(group, gIndex) in formData.groups" :key="group.uuid || gIndex">
            <!-- Group Header Row -->
            <tr class="table-primary fw-bold">
              <td colspan="4">
                <div class="d-flex align-items-center">
                  <el-select v-model="group.groupName" placeholder="Выберите группу" size="small" style="width: 500px" filterable>
                    <el-option v-for="p in getAvailableGroups(group.groupName)" :key="p.id" :label="p.nameRu" :value="p.nameRu"></el-option>
                  </el-select>
                </div>
              </td>
              <td>{{ formatNumber(getGroupCostPerUnit(group)) }}</td>
              <td>{{ formatNumber(group.calculatedSum) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'janSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'febSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'marSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'aprSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'maySum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'junSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'julSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'augSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'sepSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'octSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'novSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'decSum')) }}</td>
              <td v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)">
                <el-button type="danger" size="small" @click="removeGroup(gIndex)">
                  <font-awesome-icon :icon="['fas', 'trash']" />
                </el-button>
              </td>
            </tr>

            <tr v-for="(item, index) in group.details" :key="item.uuid || index">
              <td></td>
              <td>
                <el-select v-model="item.productName" placeholder="Выберите продукт" size="small" style="width: 100%" filterable>
                  <el-option v-for="pi in getProductItemsByGroupName(group.groupName)" :key="pi.id" :label="pi.nameRu" :value="pi.nameRu"></el-option>
                </el-select>
              </td>
              <td><el-input v-model="item.count" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td>
                <el-select v-model="item.periodicity" placeholder="Выберите" size="small" style="width: 100%">
                  <el-option label="Годовая" value="YEARLY"></el-option>
                  <el-option label="Единица" value="UNIT"></el-option>
                </el-select>
              </td>
              <td class="text-center align-middle" style="background-color: var(--el-fill-color-light); color: var(--el-text-color-regular);">{{ formatNumber(item.costPerUnit) }}</td>
              <td class="text-center align-middle" style="background-color: var(--el-fill-color-light); color: var(--el-text-color-regular);">{{ formatNumber(item.calculatedSum) }}</td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="0"><el-input v-model="item.janSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="1"><el-input v-model="item.febSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="2"><el-input v-model="item.marSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="3"><el-input v-model="item.aprSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="4"><el-input v-model="item.maySum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="5"><el-input v-model="item.junSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="6"><el-input v-model="item.julSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="7"><el-input v-model="item.augSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="8"><el-input v-model="item.sepSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="9"><el-input v-model="item.octSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="10"><el-input v-model="item.novSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="11"><el-input v-model="item.decSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)">
                <el-button type="danger" size="small" @click="removeRow(group, index)">
                  <font-awesome-icon :icon="['fas', 'trash']" />
                </el-button>
              </td>
            </tr>

            <tr v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)">
              <td :colspan="19">
                <el-button type="success" size="small" @click="addRow(group)">
                  <font-awesome-icon :icon="['fas', 'plus']"/> Добавить продукт
                </el-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mb-3" v-if="formData.status !== 'APPROVED' && hasAccess(editRoles) && formData.groups.length < dictItProducts.length">
        <el-button type="primary" size="small" @click="addGroup">
          <font-awesome-icon :icon="['fas', 'folder-plus']"/> Добавить новую группу
        </el-button>
      </div>

    </el-card>
    <vue-loading :active.sync="isLoading" :is-full-page="true"></vue-loading>
  </div>
</template>

<script>
import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import {showErrorNotify, showSuccessNotify} from "@/utils/notificationUtils";
import ueItService from "@/services/ueItService";
import dictionaryService from "@/services/dictionaryService";
import authService from "@/services/authService";
import {handleGridKeydown} from "@/utils/gridKeyboardNav";

export default {
  name: "UeItEditView",
  components: { VueLoading },
  data() {
    return {
      isLoading: false,
      saving: false,
      uuid: this.$route.params.uuid,
      formData: {
        groups: []
      },
      dictItProducts: [],
      dictItProductItems: [],
      editRoles: ['ROLE_VO_UNIVERSITY', 'ROLE_VO_ADMIN', 'ROLE_VO_MANAGER'],
      monthFields: ['janSum', 'febSum', 'marSum', 'aprSum', 'maySum', 'junSum', 'julSum', 'augSum', 'sepSum', 'octSum', 'novSum', 'decSum'],
      grandTotal: { total: 0, costPerUnit: 0, janSum: 0, febSum: 0, marSum: 0, aprSum: 0, maySum: 0, junSum: 0, julSum: 0, augSum: 0, sepSum: 0, octSum: 0, novSum: 0, decSum: 0 }
    };
  },
  mounted() {
    this.loadDictionaries();
    this.loadData();
  },
  methods: {
    hasAccess(roles) {
      return authService.hasAccess(roles);
    },
    getAvailableGroups(currentGroupName) {
      const usedNames = this.formData.groups.map(g => g.groupName).filter(n => n && n !== currentGroupName);
      return this.dictItProducts.filter(p => !usedNames.includes(p.nameRu));
    },
    getProductItemsByGroupName(groupName) {
      if (!groupName) return [];
      const group = this.dictItProducts.find(p => p.nameRu === groupName);
      if (!group) return [];
      return this.dictItProductItems.filter(item => item.dictItProductId === group.id);
    },
    formatNumber(val) {
      if (val === undefined || val === null || val === '' || isNaN(val)) return '0,00';
      const formatted = parseFloat(val).toFixed(2);
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join(',');
    },
    formatInputNumber(value) {
      if (value === undefined || value === null || value === '') return '';
      const parts = value.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join(',');
    },
    parseInputNumber(value) {
      if (!value) return '';
      let parsed = value.toString().replace(',', '.');
      parsed = parsed.replace(/[^\d.]/g, '');
      const parts = parsed.split('.');
      if (parts.length > 2) {
        parsed = parts[0] + '.' + parts.slice(1).join('');
      }
      return parsed;
    },
    async loadDictionaries() {
      try {
        const [products, items] = await Promise.all([
          dictionaryService.getItProducts(),
          dictionaryService.getItProductItems()
        ]);
        this.dictItProducts = products;
        this.dictItProductItems = items;
      } catch (e) {
        showErrorNotify('Ошибка загрузки справочников');
      }
    },
    async loadData() {
      if (!this.uuid) return;
      this.isLoading = true;
      try {
        const data = await ueItService.getById(this.uuid);
        if (data) {
          if (!data.groups) data.groups = [];
          this.formData = data;
          this.recalcGrandTotal();
        }
      } catch (e) {
        showErrorNotify('Ошибка при загрузке данных');
      } finally {
        this.isLoading = false;
      }
    },
    recalcTotal(item, group) {
      const months = ["janSum", "febSum", "marSum", "aprSum", "maySum", "junSum", "julSum", "augSum", "sepSum", "octSum", "novSum", "decSum"];
      const sum = months.reduce((acc, m) => acc + (parseFloat(item[m]) || 0), 0);
      item.calculatedSum = sum.toFixed(2);
      const count = parseFloat(item.count) || 0;
      item.costPerUnit = count > 0 ? (sum / count).toFixed(2) : '0';
      group.calculatedSum = group.details.reduce((s, d) => s + (parseFloat(d.calculatedSum) || 0), 0).toFixed(2);
      this.recalcGrandTotal();
    },
    recalcGrandTotal() {
      const gt = {};
      for (const f of this.monthFields) { gt[f] = 0; }
      gt.total = 0;
      gt.costPerUnit = 0;
      for (const group of this.formData.groups) {
        for (const f of this.monthFields) {
          gt[f] += parseFloat(this.getGroupMonthSum(group, f)) || 0;
        }
        gt.total += parseFloat(group.calculatedSum) || 0;
        if (group.details) {
          for (const d of group.details) {
            gt.costPerUnit += parseFloat(d.costPerUnit) || 0;
          }
        }
      }
      this.grandTotal = gt;
    },
    getGroupCostPerUnit(group) {
      if (!group || !group.details) return 0;
      return group.details.reduce((s, d) => s + (parseFloat(d.costPerUnit) || 0), 0).toFixed(2);
    },
    getGroupMonthSum(group, monthName) {
      if (!group || !group.details) return 0;
      return group.details.reduce((s, d) => s + (parseFloat(d[monthName]) || 0), 0).toFixed(2);
    },
    addGroup() {
      this.formData.groups.push({
        groupName: '',
        calculatedSum: 0,
        details: []
      });
    },
    addRow(group) {
      if (!group.details) group.details = [];
      group.details.push({
        dictItProductId: null,
        productName: '',
        periodicity: '',
        count: '',
        costPerUnit: '',
        calculatedSum: '',
        janSum: '', febSum: '', marSum: '', aprSum: '',
        maySum: '', junSum: '', julSum: '', augSum: '',
        sepSum: '', octSum: '', novSum: '', decSum: ''
      });
    },
    removeGroup(index) {
      if (confirm('Удалить группу и все её строки?')) {
        this.formData.groups.splice(index, 1);
        this.recalcGrandTotal();
      }
    },
    removeRow(group, index) {
      if (confirm('Удалить строку?')) {
        group.details.splice(index, 1);
        group.calculatedSum = group.details.reduce((s, d) => s + (parseFloat(d.calculatedSum) || 0), 0).toFixed(2);
        this.recalcGrandTotal();
      }
    },
    async saveData() {
      this.saving = true;
      try {
        const result = await ueItService.save(this.formData);
        this.formData = result;
        showSuccessNotify('Данные успешно сохранены');
      } catch (e) {
        showErrorNotify('Ошибка при сохранении');
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.push('/expenses/it');
    },
    handleKeydown(event) {
      handleGridKeydown(event, this.$el);
    },
    handlePaste(event) {
      const target = event.target;
      if (!target || target.tagName !== 'INPUT') return;

      const td = target.closest('td');
      if (!td) return;

      const startGroupIdx = parseInt(td.dataset.groupIdx);
      const startItemIdx = parseInt(td.dataset.itemIdx);
      const startCol = parseInt(td.dataset.col);

      if (isNaN(startGroupIdx) || isNaN(startItemIdx) || isNaN(startCol)) return;

      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      const text = clipboardData.getData('text/plain');
      if (!text) return;

      const rows = text.split(/\r?\n/).filter(r => r.trim() !== '');
      if (rows.length <= 1 && !rows[0].includes('\t')) return;

      event.preventDefault();

      const MONTH_FIELDS = ["janSum", "febSum", "marSum", "aprSum", "maySum", "junSum", "julSum", "augSum", "sepSum", "octSum", "novSum", "decSum"];

      let currentGroupIdx = startGroupIdx;
      let currentItemIdx = startItemIdx;

      for (let r = 0; r < rows.length; r++) {
        const cols = rows[r].split('\t');
        
        if (currentGroupIdx >= this.formData.groups.length) break;
        let group = this.formData.groups[currentGroupIdx];
        
        while (currentItemIdx >= group.details.length) {
          currentGroupIdx++;
          currentItemIdx = 0;
          if (currentGroupIdx >= this.formData.groups.length) break;
          group = this.formData.groups[currentGroupIdx];
        }
        
        if (currentGroupIdx >= this.formData.groups.length) break;

        const currentItem = group.details[currentItemIdx];

        for (let c = 0; c < cols.length; c++) {
          const colIdx = startCol + c;
          if (colIdx >= MONTH_FIELDS.length) break;

          const rawVal = String(cols[c]).replace(/\s/g, '').replace(',', '.');
          const num = parseFloat(rawVal);
          currentItem[MONTH_FIELDS[colIdx]] = isNaN(num) || num === 0 ? '' : num;
        }

        this.recalcTotal(currentItem, group);
        currentItemIdx++;
      }

      this.$forceUpdate();
    }
  }
};
</script>

<style scoped>
.grid-table th { 
  font-size: 13px; 
  vertical-align: middle; 
  text-align: center; 
}
.grid-table td { 
  font-size: 13px;
  vertical-align: middle; 
}
.calculated-row {
  background-color: #e8f5e9 !important;
}
.total-row td { font-weight: bold; text-align: right; }
.total-row td:first-child { text-align: left; }
:deep(.el-input__inner) {
  font-size: 13px;
}
:deep(.el-select .el-input__inner) {
  font-size: 13px;
}
</style>
